import { formatStringToNumber } from 'helpers/formatData';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// import PapaParse from 'papaparse';

import {
  downloadReportsService,
  getRevenueChartsService,
  getUsersStatesChartsService,
  getRecurringRevenueChartsService,
  getUsersAcquiredChartsService,
} from 'services/reports.service';

import {
  getRevenueReports,
  getRecurringRevenueReports,
  getUserAcquiredReports,
  getUsersByStateReports,
  getUsersByAgeReports,
  getMostLikedProperties,
  getMostTradedProperties,
  getMostAppreciatedProperties,
  resetReports,
} from 'store/reducers/reportsSlice';
import { toast } from 'react-toastify';
import { downloadCSV } from 'helpers/fileDownload';

const useReportsHook = ({
  currentPage,
  perPage,
  sortBy,
  searchTerm,
  reportType,
  duration,
  // columns,
  // rows,
}) => {
  const [isLoading, setLoading] = useState(false);
  const [isFetchingChart, setFetchingChart] = useState(false);

  const [chartResponse, setChartResponse] = useState([]);
  const [statesDataChart, setStatesDataChart] = useState(null);

  const dispatch = useDispatch();
  const { reportsList } = useSelector((state) => state.reports);

  useEffect(() => {
    setChartResponse([]);
    setStatesDataChart(null);

    return () => {
      dispatch(resetReports());
    };
  }, [reportType]);

  async function fetchReportsData() {
    try {
      setLoading(true);

      const filtersObject = {
        page: currentPage + 1,
        limit: perPage,
        sortField: sortBy?.id ?? 'id',
        sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
        duration,
        //    search: '',
        //    userId,
        //    propertyId,
        //    ...filterObj,
      };

      switch (reportType) {
        case 'revenue-report': {
          await dispatch(getRevenueReports(filtersObject));
          break;
        }

        case 'recurring-revenue': {
          await dispatch(getRecurringRevenueReports(filtersObject));
          break;
        }

        case 'users-acquired': {
          await dispatch(getUserAcquiredReports(filtersObject));
          break;
        }

        case 'users-by-state': {
          await dispatch(getUsersByStateReports(filtersObject));
          break;
        }

        case 'users-by-age': {
          await dispatch(getUsersByAgeReports(filtersObject));
          break;
        }

        case 'most-liked': {
          await dispatch(getMostLikedProperties(filtersObject));
          break;
        }
        case 'most-appreciated': {
          await dispatch(getMostAppreciatedProperties(filtersObject));

          break;
        }
        case 'most-traded': {
          await dispatch(getMostTradedProperties(filtersObject));
          break;
        }

        default:
          break;
      }
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const getTabTitle = useCallback(() => {
    switch (reportType) {
      case 'users-acquired':
        return 'New Users Acquired';

      case 'users-by-state':
        return 'Users by State';

      case 'users-by-age':
        return 'Users by Age Group';

      case 'most-liked':
        return 'Most Liked Properties';

      case 'most-appreciated':
        return 'Most Appreciated Properties';

      case 'most-traded':
        return 'Most Traded Properties';

      default:
        return 'New Users Acquired';
    }
  }, [reportType]);

  async function fetchRevenueReportCharts() {
    try {
      setFetchingChart(true);
      const response = await getRevenueChartsService();
      const formattedArray = Array.from(
        Array(12).keys(),
        (month) =>
          response?.data?.data?.monthWiseRevenueData.find(
            (data) => +data?.month === month + 1
          ) || {
            month: String(month + 1),
            totalRevenue: 0,
          }
      )?.map((data) => formatStringToNumber(data?.totalRevenue));
      setChartResponse(formattedArray ?? []);
    } catch (error) {
      console.log({ error });
    } finally {
      setFetchingChart(false);
    }
  }

  async function fetchRecurringRevenueCharts() {
    try {
      setFetchingChart(true);
      const response = await getRecurringRevenueChartsService();

      const formattedArray = Array.from(
        Array(12).keys(),
        (month) =>
          response?.data?.data?.recurringRevenueData?.find(
            (data) => +data?.month === month + 1
          ) || {
            month: String(month + 1),
            totalRecurringRevenue: 0,
          }
      )?.map((data) => formatStringToNumber(data?.totalRecurringRevenue));
      setChartResponse(formattedArray ?? []);
    } catch (error) {
      console.log({ error });
    } finally {
      setFetchingChart(false);
    }
  }

  async function fetchUserAcquiredCharts() {
    try {
      setFetchingChart(true);
      const response = await getUsersAcquiredChartsService();

      const formattedArray = Array.from(
        Array(12).keys(),
        (month) =>
          response?.data?.data?.monthWiseUserAcquiredData?.find(
            (data) => +data?.month === month + 1
          ) || {
            month: String(month + 1),
            count: 0,
          }
      )?.map((data) => formatStringToNumber(data?.count));
      setChartResponse(formattedArray ?? []);
    } catch (error) {
      console.log({ error });
    } finally {
      setFetchingChart(false);
    }
  }

  async function fetchUserStatesCharts() {
    try {
      setFetchingChart(true);
      const response = await getUsersStatesChartsService();

      const formattedArray = response?.data?.data?.stateUserData?.map(
        (data) => ({
          state: data?.state ?? 'NA',
          userCount: formatStringToNumber(data?.userCount),
        })
      );

      setStatesDataChart(formattedArray ?? []);
    } catch (error) {
      console.log({ error });
    } finally {
      setFetchingChart(false);
    }
  }

  // const getReportName = () => {
  //   switch (reportType) {
  //     case 'revenue-report': {
  //       return 'Revenue';
  //     }

  //     case 'recurring-revenue': {
  //       return 'Recurring Revenue';
  //     }

  //     case 'users-acquired': {
  //       return 'User Acquired';
  //     }

  //     case 'users-by-state': {
  //       return 'User by state';
  //     }

  //     case 'users-by-age': {
  //       return 'User by age';
  //     }

  //     case 'most-liked': {
  //       return 'Most liked property';
  //     }
  //     case 'most-appreciated': {
  //       return 'Most appreciated property';
  //     }
  //     case 'most-traded': {
  //       return 'Most traded property';
  //     }

  //     default:
  //       break;
  //   }
  // };

  // const fetchDownloadTransactions = async () => {
  //   try {
  //     const response = await downloadReportsService(
  //       getReportName(),
  //       sortBy?.id ?? 'id',
  //       sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
  //       '',
  //       duration
  //     );

  //     console.log({ response });

  //     let reports = [];

  //     switch (reportType) {
  //       case 'revenue-report': {
  //         reports = response?.data?.data?.recurringRevenueData ?? [];
  //         break;
  //       }

  //       case 'recurring-revenue': {
  //         reports = response?.data?.data?.recurringRevenueData ?? [];
  //         break;
  //       }

  //       case 'users-acquired': {
  //         reports = response?.data?.data?.recurringRevenueData ?? [];
  //         break;
  //       }

  //       case 'users-by-state': {
  //         reports = response?.data?.data?.recurringRevenueData ?? [];
  //         break;
  //       }

  //       case 'users-by-age': {
  //         reports = response?.data?.data?.recurringRevenueData ?? [];
  //         break;
  //       }

  //       case 'most-liked': {
  //         reports = response?.data?.data?.recurringRevenueData ?? [];
  //         break;
  //       }
  //       case 'most-appreciated': {
  //         reports = response?.data?.data?.recurringRevenueData ?? [];

  //         break;
  //       }
  //       case 'most-traded': {
  //         reports = response?.data?.data?.recurringRevenueData ?? [];
  //         break;
  //       }

  //       default:
  //         reports = [];
  //         break;
  //     }

  //     return reports;
  //   } catch (error) {
  //     console.log(error);
  //     // return [];
  //     throw error;
  //   }
  // };

  // const onDownloadCSV = async () => {
  //   try {
  //     const transactions = await fetchDownloadTransactions();
  //     const csv = '';
  //     // const csv = PapaParse.unparse(transactions.map(rows()), {
  //     //   header: false,
  //     // });

  //     console.log({ csv });

  //     const headerParser = PapaParse.unparse(
  //       {
  //         fields: columns.map((col) => col.Header),
  //         data: [],
  //       },
  //       {
  //         header: true,
  //       }
  //     );

  //     const formattedCSVData = headerParser + csv;

  //     if (!transactions?.length) {
  //       toast.warn(
  //         `Unable to download file as there are no records to export!`
  //       );
  //       return;
  //     }

  //     await downloadCSV(formattedCSVData, `${getReportName()}-Report`);
  //   } catch (error) {
  //     console.log({
  //       error,
  //     });
  //     toast.error(error?.message ?? 'Something went wrong!');
  //   }
  // };

  return {
    isLoading,
    fetchReportsData,
    reportsList,
    getTabTitle,
    isFetchingChart,
    chartResponse,
    fetchRevenueReportCharts,
    fetchRecurringRevenueCharts,
    fetchUserAcquiredCharts,
    fetchUserStatesCharts,
    statesDataChart,
    // onDownloadCSV,
  };
};

export default useReportsHook;
